// src/pages/ResetPasswordPage.js
import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useAuth from "../hooks/useAuth";

// Using EyeIcon and EyeSlashIcon similar to LoginPage for password visibility
const EyeIcon = ({ className }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-.274.992-.686 1.929-1.188 2.791M12 17.75A10.946 10.946 0 012.458 12"
    />
  </svg>
);

const EyeSlashIcon = ({ className }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 .594-1.72 1.56-3.244 2.768-4.478M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2 2l20 20"
    />
  </svg>
);


const ResetPasswordPage = () => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const auth = useAuth();

  const messageRef = useRef(null); // For focusing on success/error messages

  // Extract code and email from URL parameters
  const resetCode = searchParams.get("code");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!resetCode || !email) {
      setError("Invalid reset link. Parameters missing. Please request a new password reset link.");
      // No setIsLoading(false) here as it's handled by the main submit flow or initial render
    }
  }, [resetCode, email]);

  useEffect(() => {
    if ((error || successMessage) && messageRef.current) {
      messageRef.current.focus();
    }
  }, [error, successMessage]);


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!resetCode || !email) {
      setError("Cannot reset password. Link parameters are missing.");
      setIsLoading(false); // Ensure loading is stopped
      return;
    }
    setIsLoading(true);


    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    // Password complexity rules should ideally match backend
    // Example: Minimum 8 characters, one uppercase, one lowercase, one number, one special character
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError("Password must be at least 8 characters long and include uppercase, lowercase, a number, and a special character.");
      setIsLoading(false);
      return;
    }


    try {
      const response = await auth.resetPassword(
        email,
        resetCode,
        formData.password
      );
      setSuccessMessage(
        response.message ||
          "Password reset successful! You can now login with your new password."
      );
    } catch (err) {
      setError(
        err.details?.message ||
          err.message ||
          "Password reset failed. Please try again or request a new reset link."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>

      <div className="relative max-w-md w-full">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
              <svg
                aria-hidden="true"
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2m0 0V7a2 2 0 012-2h6a2 2 0 012 2v2M9 12h6"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-slate-100 mb-2"> {/* Contrast Fix */}
              Reset Password
            </h2>
            <p className="text-slate-300"> {/* Contrast Fix */}
              Enter your new password for{" "}
              <span className="text-sky-300 font-medium">{email || "your account"}</span> {/* Contrast Fix & fallback */}
            </p>
          </div>

          {successMessage ? (
            <div role="status" aria-live="polite" ref={messageRef} tabIndex={-1} className="bg-green-700/20 border border-green-600/30 rounded-xl p-6 text-center outline-none"> {/* Contrast Fix, ARIA, Focus */}
              <div className="w-12 h-12 bg-green-600/30 rounded-full flex items-center justify-center mx-auto mb-4"> {/* Contrast Fix */}
                <svg
                  aria-hidden="true"
                  className="w-6 h-6 text-green-300" /* Contrast Fix */
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-green-200 mb-4">{successMessage}</p> {/* Contrast Fix */}
              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Continue to Login
                <svg
                  aria-hidden="true"
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-slate-200 mb-2" /* Contrast Fix */
                >
                  New Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 pr-10" /* Contrast Fix for placeholder */
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 top-7 pr-3 flex items-center text-slate-400 hover:text-slate-200"
                  aria-label={showPassword ? "Hide new password" : "Show new password"}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
                <p className="text-xs text-slate-400 mt-1"> {/* Contrast Fix */}
                  Min. 8 characters, with uppercase, lowercase, number, & symbol.
                </p>
              </div>

              <div className="relative">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-slate-200 mb-2" /* Contrast Fix */
                >
                  Confirm New Password
                </label>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  id="confirmPassword"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 pr-10" /* Contrast Fix for placeholder */
                  required
                  autoComplete="new-password"
                />
                 <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 top-7 pr-3 flex items-center text-slate-400 hover:text-slate-200"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>

              {error && (
                // Initial parameter error is handled by disabling button and potentially this message
                <div role="alert" aria-live="assertive" ref={messageRef} tabIndex={-1} className="bg-red-700/20 border border-red-600/30 rounded-xl p-4 outline-none"> {/* Contrast Fix, ARIA, Focus */}
                  <div className="flex items-center">
                    <svg
                      aria-hidden="true"
                      className="w-5 h-5 text-red-300 mr-2" /* Contrast Fix */
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-red-200 text-sm">{error}</p> {/* Contrast Fix */}
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !resetCode || !email} // Button disabled if params missing
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none" // Adjusted disabled opacity
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      aria-hidden="true"
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Resetting Password...
                  </div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
          )}

          <div className="mt-8 text-center">
            <p className="text-slate-300"> {/* Contrast Fix */}
              Remember your password?{" "}
              <Link
                to="/login"
                className="text-sky-400 hover:text-sky-300 font-semibold hover:underline transition-colors" /* Contrast Fix */
              >
                Sign in
              </Link>
            </p>
            <p className="text-slate-400 text-sm mt-2"> {/* Contrast Fix */}
              Need help?{" "}
              <Link
                to="/forgot-password"
                className="text-sky-400 hover:text-sky-300 hover:underline transition-colors" /* Contrast Fix */
              >
                Request new reset link
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
